import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X, Maximize2, Minimize2 } from "lucide-react"
import { cn } from "../../lib/utils"

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const [position, setPosition] = React.useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = React.useState(false)
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 })
  const [size, setSize] = React.useState({ width: 'auto', height: 'auto' })
  const [isMaximized, setIsMaximized] = React.useState(false)
  const dialogRef = React.useRef(null)

  const handleMouseDown = (e) => {
    if (e.target.closest('.dialog-header') && !e.target.closest('button')) {
      setIsDragging(true)
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      })
    }
  }

  const handleMouseMove = React.useCallback((e) => {
    if (isDragging && !isMaximized) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      })
    }
  }, [isDragging, dragStart, isMaximized])

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove])

  const toggleMaximize = () => {
    if (isMaximized) {
      setIsMaximized(false)
      setPosition({ x: 0, y: 0 })
    } else {
      setIsMaximized(true)
      setPosition({ x: 0, y: 0 })
    }
  }

  const resetPosition = () => {
    setPosition({ x: 0, y: 0 })
    setIsMaximized(false)
  }

  // Reset position when dialog opens
  React.useEffect(() => {
    resetPosition()
  }, [])

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={(node) => {
          dialogRef.current = node
          if (typeof ref === 'function') ref(node)
          else if (ref) ref.current = node
        }}
        onMouseDown={handleMouseDown}
        className={cn(
          "fixed z-50 gap-4 bg-white p-6 shadow-lg transition-all duration-200",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          isMaximized 
            ? "inset-4 rounded-lg" 
            : "left-1/2 top-1/2 w-full max-w-3xl max-h-[90vh] rounded-lg border",
          !isMaximized && "translate-x-[-50%] translate-y-[-50%]",
          isDragging && "cursor-grabbing select-none",
          className
        )}
        style={!isMaximized ? {
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
        } : undefined}
        {...props}
      >
        <div className="dialog-header cursor-grab flex items-center justify-between -m-6 mb-4 p-4 bg-slate-50 rounded-t-lg border-b">
          <div className="flex-1 text-sm text-slate-500">
            {!isMaximized && <span className="select-none">Drag untuk menggeser</span>}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={toggleMaximize}
              className="p-1.5 hover:bg-slate-200 rounded transition-colors"
              title={isMaximized ? "Perkecil" : "Perbesar"}
            >
              {isMaximized ? (
                <Minimize2 className="h-4 w-4 text-slate-600" />
              ) : (
                <Maximize2 className="h-4 w-4 text-slate-600" />
              )}
            </button>
            <DialogPrimitive.Close className="p-1.5 hover:bg-slate-200 rounded transition-colors">
              <X className="h-4 w-4 text-slate-600" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>
        </div>
        <div className={cn("overflow-y-auto", isMaximized ? "max-h-[calc(100vh-120px)]" : "max-h-[calc(90vh-120px)]")}>
          {children}
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
